import { apiService } from './api';
import {
  RentalUnitUser,
  CreateTenantApplicationData,
  TenantFilters,
  ApiResponse,
  PaginatedResponse
} from '../types';

export const tenantService = {
  // Basic tenant operations
  async getTenants(filters?: TenantFilters): Promise<PaginatedResponse<RentalUnitUser>> {
    const params = new URLSearchParams();
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          if (Array.isArray(value)) {
            // Handle array values (companyIds, propertyIds)
            value.forEach(item => params.append(`${key}[]`, String(item)));
          } else {
            params.append(key, String(value));
          }
        }
      });
    }

    const response = await apiService.get(`/tenants?${params.toString()}`);
    return response.data;
  },

  async getTenant(id: string): Promise<ApiResponse<RentalUnitUser>> {
    const response = await apiService.get(`/tenants/${id}`);
    return response.data;
  },

  async createTenantApplication(data: CreateTenantApplicationData): Promise<ApiResponse<RentalUnitUser>> {
    const response = await apiService.post('/tenants', data);
    return response.data;
  },

  async updateTenantApplication(id: string, data: any): Promise<ApiResponse<RentalUnitUser>> {
    const response = await apiService.put(`/tenants/${id}`, data);
    return response.data;
  },

  // Application management
  async approveTenantApplication(id: string, data?: {
    approvalNotes?: string;
    moveInDate?: string;
  }): Promise<ApiResponse<RentalUnitUser>> {
    const response = await apiService.put(`/tenants/${id}/approve`, data);
    return response.data;
  },

  async rejectTenantApplication(id: string, rejectionReason: string): Promise<ApiResponse<RentalUnitUser>> {
    const response = await apiService.put(`/tenants/${id}/reject`, { rejectionReason });
    return response.data;
  },

  // Filtering and search
  async getTenantsByStatus(status: string, companyId?: string): Promise<ApiResponse<RentalUnitUser[]>> {
    const params = new URLSearchParams({ status });
    if (companyId) params.append('companyId', companyId);

    const response = await apiService.get(`/tenants/status/${status}?${params.toString()}`);
    return response.data;
  },

  async getTenantsByCompany(companyId: string, filters?: TenantFilters): Promise<PaginatedResponse<RentalUnitUser>> {
    const params = new URLSearchParams();
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, String(value));
        }
      });
    }

    const response = await apiService.get(`/tenants/company/${companyId}?${params.toString()}`);
    return response.data;
  },

  // Financial management
  async getTenantPaymentHistory(id: string): Promise<ApiResponse<any[]>> {
    const response = await apiService.get(`/tenants/${id}/payments`);
    return response.data;
  },

  async getTenantOutstandingDues(id: string): Promise<ApiResponse<{
    rent: number;
    maintenance: number;
    utilities: number;
    penalties: number;
    total: number;
  }>> {
    const response = await apiService.get(`/tenants/${id}/dues`);
    return response.data;
  },

  async updateTenantFinancials(id: string, data: {
    depositAmount?: { paid: number; pending: number };
    outstandingDues?: {
      rent?: number;
      maintenance?: number;
      utilities?: number;
      penalties?: number;
    };
    lastRentPaid?: string;
  }): Promise<ApiResponse<RentalUnitUser>> {
    const response = await apiService.put(`/tenants/${id}/financials`, data);
    return response.data;
  },

  // Notes and communication
  async addTenantNote(id: string, note: {
    note: string;
    type: 'general' | 'important' | 'warning';
  }): Promise<ApiResponse<RentalUnitUser>> {
    const response = await apiService.post(`/tenants/${id}/notes`, {
      content: note.note,
      type: note.type
    });
    return response.data;
  },

  // Compliance and verification
  async updateTenantCompliance(id: string, data: {
    policeVerification?: {
      status: string;
      completedDate?: string;
      referenceNumber?: string;
    };
    backgroundCheck?: {
      status: string;
      completedDate?: string;
      score?: number;
    };
  }): Promise<ApiResponse<RentalUnitUser>> {
    const response = await apiService.put(`/tenants/${id}/compliance`, data);
    return response.data;
  },

  // Document management
  async getTenantDocuments(id: string): Promise<ApiResponse<{
    type: string;
    url: string;
    verified: boolean;
    verifiedBy?: string;
    verifiedAt?: string;
    uploadedAt: string;
  }[]>> {
    const response = await apiService.get(`/tenants/${id}/documents`);
    return response.data;
  },

  async getDocumentVault(
    userId: string,
    companyId: string,
    options?: { search?: string; page?: number; limit?: number; sortOrder?: 'asc' | 'desc' }
  ): Promise<ApiResponse<{
    documents: Array<{
      _id?: string;
      type: string;
      url?: string;
      text?: string;
      uploadedAt: string;
    }>;
    page: number;
    limit: number;
    total: number;
    hasMore: boolean;
  }>> {
    const params = new URLSearchParams({
      userId,
      companyId
    });
    if (options?.search) {
      params.append('search', options.search);
    }
    if (options?.page) {
      params.append('page', String(options.page));
    }
    if (options?.limit) {
      params.append('limit', String(options.limit));
    }
    if (options?.sortOrder) {
      params.append('sortOrder', options.sortOrder);
    }
    const response = await apiService.get(`/tenants/document-vault?${params.toString()}`);
    return response.data;
  },

  async uploadTenantDocument(id: string, file: File, type: string): Promise<ApiResponse<{
    type: string;
    url: string;
    uploadedAt: string;
  }>> {
    const formData = new FormData();
    formData.append('document', file);
    formData.append('type', type);

    const response = await apiService.post(`/tenants/${id}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  async addTenantDocumentByUrl(id: string, documentData: { type: string; url?: string; text?: string }): Promise<ApiResponse<any>> {
    const response = await apiService.post(`/tenants/${id}/documents`, documentData);
    return response.data;
  },

  async verifyTenantDocument(id: string, documentId: string, verified: boolean): Promise<ApiResponse> {
    const response = await apiService.put(`/tenants/${id}/documents/${documentId}`, { verified });
    return response.data;
  },

  async updateTenantDocument(id: string, documentId: string, data: { type?: string; text?: string; url?: string; oldUrl?: string }): Promise<ApiResponse> {
    const response = await apiService.put(`/tenants/${id}/documents/${documentId}`, data);
    return response.data;
  },

  async deleteTenantDocument(id: string, documentId: string): Promise<ApiResponse> {
    const response = await apiService.delete(`/tenants/${id}/documents/${documentId}`);
    return response.data;
  },

  // Behavior and ratings
  async getTenantBehaviorRecord(id: string): Promise<ApiResponse<{
    ratings: {
      cleanliness?: number;
      cooperation?: number;
      timelyPayment?: number;
      overallRating?: number;
    };
    complaints: {
      date: string;
      description: string;
      severity: string;
      resolved: boolean;
    }[];
    warnings: {
      date: string;
      reason: string;
      givenBy: string;
    }[];
  }>> {
    const response = await apiService.get(`/tenants/${id}/behavior`);
    return response.data;
  },

  async addTenantComplaint(id: string, complaint: {
    description: string;
    severity: 'low' | 'medium' | 'high';
  }): Promise<ApiResponse<RentalUnitUser>> {
    const response = await apiService.post(`/tenants/${id}/complaints`, complaint);
    return response.data;
  },

  async addTenantWarning(id: string, warning: {
    reason: string;
  }): Promise<ApiResponse<RentalUnitUser>> {
    const response = await apiService.post(`/tenants/${id}/warnings`, warning);
    return response.data;
  },

  async updateTenantRating(id: string, ratings: {
    cleanliness?: number;
    cooperation?: number;
    timelyPayment?: number;
    overallRating?: number;
  }): Promise<ApiResponse<RentalUnitUser>> {
    const response = await apiService.put(`/tenants/${id}/rating`, { ratings });
    return response.data;
  },

  // Tenancy management
  async moveTenantOut(id: string, data: {
    moveOutDate: string;
    reason?: string;
    finalSettlement?: {
      depositRefund: number;
      outstandingCharges: number;
      damages: number;
    };
  }): Promise<ApiResponse<RentalUnitUser>> {
    const response = await apiService.put(`/tenants/${id}/move-out`, data);
    return response.data;
  },

  async extendTenantLease(id: string, data: {
    newEndDate: string;
    rentIncrease?: number;
    terms?: string;
  }): Promise<ApiResponse<RentalUnitUser>> {
    const response = await apiService.put(`/tenants/${id}/extend-lease`, data);
    return response.data;
  },

  async getEligibleUnitsForTransfer(id: string): Promise<ApiResponse<{
    currentUnit: { _id: string; unitNumber: string; propertyName: string };
    eligibleUnits: Array<{
      propertyId: string;
      propertyName: string;
      address: any;
      buildings: Record<string, Array<{
        _id: string;
        unitNumber: string;
        unitType: string;
        floor: string;
        pricing: any;
        status: string;
      }>>;
    }>;
    totalCount: number;
  }>> {
    const response = await apiService.get(`/tenants/${id}/transfer/eligible-units`);
    return response.data;
  },

  async getTransferPreview(id: string, newUnitId: string, transferOptions?: any): Promise<ApiResponse<{
    tenant: any;
    currentUnit: any;
    newUnit: any;
    financials: {
      outstandingDues: any;
      totalOutstanding: number;
      currentDepositPaid: number;
      newDepositRequired: number;
      depositDifference: number;
      depositDifferenceText: string;
      pendingInvoicesCount: number;
    };
    meterReadings: any;
    coTenants: {
      count: number;
      requiresNewPrimary: boolean;
      list: Array<{ _id: string; name: string; email: string; tenancyType: string }>;
    };
    warnings: Array<{ type: string; message: string }>;
    canTransfer: boolean;
  }>> {
    const response = await apiService.post(`/tenants/${id}/transfer/preview`, { 
      newUnitId, 
      transferOptions 
    });
    return response.data;
  },

  async transferTenant(id: string, data: {
    newUnitId: string;
    transferDate?: string;
    reason?: string;
    newAgreementDetails?: {
      rentAmount?: number;
      securityDeposit?: number;
      maintenanceCharges?: number;
      rentDueDate?: number;
    };
    depositHandling?: 'transfer' | 'settle_and_collect' | 'adjust_difference';
    generateFinalInvoice?: boolean;
    finalMeterReadings?: {
      electricity?: number;
    };
    customCharges?: {
      electricityUnitRate?: number;
      electricityInitialReading?: number;
      otherCharges?: any[];
    };
    newPrimaryTenantId?: string;
    notifyTenant?: boolean;
  }): Promise<ApiResponse<{
    oldTenantRecord: { _id: string; status: string };
    newTenantRecord: { _id: string; unitNumber: string; propertyName: string; status: string };
    transfer: { fromUnit: string; toUnit: string; transferDate: Date; depositHandling: string; reason: string };
  }>> {
    const response = await apiService.put(`/tenants/${id}/transfer`, data);
    return response.data;
  },

  // Unified status update with unit
  async updateTenantStatusWithUnit(id: string, data: {
    status: string;
    moveOutDate?: string;
    isActive?: boolean;
    noticePeriodGiven?: string;
  }): Promise<ApiResponse<{ tenant: RentalUnitUser; unit: any }>> {
    const response = await apiService.put(`/tenants/${id}/status`, data);
    return response.data;
  },

  async activateUpcomingTenant(id: string, note?: string): Promise<ApiResponse<RentalUnitUser>> {
    const response = await apiService.put(`/tenants/${id}/activate-upcoming`, note ? { note } : {});
    return response.data;
  },

  async cancelUpcomingTenant(id: string, reason?: string): Promise<ApiResponse<RentalUnitUser>> {
    const response = await apiService.put(`/tenants/${id}/cancel-upcoming`, reason ? { reason } : {});
    return response.data;
  },

  // Helper methods
  isCurrent(tenant: RentalUnitUser): boolean {
    return tenant.isActive && ['active', 'notice_given'].includes(tenant.status);
  },

  areDocumentsComplete(tenant: RentalUnitUser): boolean {
    const requiredDocs = ['aadhaar', 'pan', 'salary_slip'];
    const submittedDocs = tenant.documents?.map(doc => doc.type) || [];
    return requiredDocs.every(doc => submittedDocs.includes(doc));
  },

  calculateTotalOutstanding(tenant: RentalUnitUser): number {
    const outstanding = tenant.financialDetails.outstandingDues;
    return outstanding.rent + outstanding.maintenance + outstanding.utilities + outstanding.penalties;
  },

  getStayDurationInDays(tenant: RentalUnitUser): number {
    const moveInDate = new Date(tenant.occupancyDetails.moveInDate);
    const moveOutDate = tenant.occupancyDetails.moveOutDate 
      ? new Date(tenant.occupancyDetails.moveOutDate) 
      : new Date();
    const diffTime = Math.abs(moveOutDate.getTime() - moveInDate.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  },

  getNextRentDueDate(tenant: RentalUnitUser): Date {
    const today = new Date();
    const dueDate = tenant.agreementDetails.rentDueDate;
    let nextDue = new Date(today.getFullYear(), today.getMonth(), dueDate);
    
    if (nextDue <= today) {
      nextDue = new Date(today.getFullYear(), today.getMonth() + 1, dueDate);
    }
    
    return nextDue;
  },

  getMonthlyTotal(tenant: RentalUnitUser): number {
    return tenant.agreementDetails.rentAmount + tenant.agreementDetails.maintenanceCharges;
  }
};
